import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { ErrorState } from '@/components/common/ErrorState';
import { PageLoader } from '@/components/common/PageLoader';
import { pcmpPendingReportQueryOptions } from '../api/pcmpReports.api';
import { PcmpPrintSheet } from '../components/PcmpPrint/PcmpPrintSheet';
import { PcmpPendingReportSheet } from '../components/PcmpReports/PcmpPendingReportSheet';
import type { PcmpPendingReportParams } from '../types/pcmpReports.types';
import { pcmpReportPeriodLabel } from '../utils/pcmpReportPeriod';

const route = getRouteApi('/_print/pcmp/reports/pending/print');

/** The Pending report on landscape A4 sheets, opening the print dialog once loaded. */
export function PcmpPendingReportPrintPage() {
  const search = route.useSearch();
  const { projectId, division, departmentId, dateFrom, dateTo, stage } = search;

  const params = useMemo<PcmpPendingReportParams>(
    () => ({ projectId, division, departmentId, dateFrom, dateTo, stage }),
    [projectId, division, departmentId, dateFrom, dateTo, stage],
  );

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    ...pcmpPendingReportQueryOptions(params),
    // Closing the print dialog refocuses the tab; keep the sheet as printed.
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (data) document.title = `Pending Report ${data.reportDate}`;
  }, [data]);

  if (isLoading) return <PageLoader />;

  if (error || !data) {
    return (
      <div className="p-6">
        <ErrorState
          error={error}
          fallbackTitle="Could not load the Pending report"
          onRetry={() => void refetch()}
          isRetrying={isFetching}
          fill
        />
      </div>
    );
  }

  return (
    <PcmpPrintSheet>
      <PcmpPendingReportSheet
        report={data}
        stage={stage}
        periodLabel={pcmpReportPeriodLabel(dateFrom, dateTo)}
      />
    </PcmpPrintSheet>
  );
}
